import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Loader2, Eye, EyeOff, CheckCircle, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RegisterPage = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [associations, setAssociations] = useState([]);
  const [selectedAssociations, setSelectedAssociations] = useState([]);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    password: '',
    confirmPassword: '',
    telefono: '',
    estado: '',
    ciudad: ''
  });

  useEffect(() => {
    fetchAssociations();
  }, []);

  async function fetchAssociations() {
    try {
      const res = await fetch(`${API}/associations`);
      if (res.ok) {
        const data = await res.json();
        setAssociations(data);
      }
    } catch (err) {
      console.error('Error fetching associations:', err);
    }
  }

  function toggleAssociation(assoc) {
    setSelectedAssociations(prev => {
      const exists = prev.find(a => a.asociacion_id === assoc.id);
      if (exists) {
        return prev.filter(a => a.asociacion_id !== assoc.id);
      } else {
        return [...prev, { asociacion_id: assoc.id, nombre: assoc.nombre, estatus_membresia: 'pendiente' }];
      }
    });
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setIsLoading(true);

    try {
      await register(
        formData.nombre, 
        formData.email, 
        formData.password, 
        formData.telefono,
        formData.estado,
        formData.ciudad,
        selectedAssociations
      );
      navigate('/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const benefits = [
    'Perfil público verificable en el directorio',
    'Certificado digital con QR verificable',
    'Descuentos en oferta académica',
    'Acceso a la red profesional CNI'
  ];

  return (
    <div className="min-h-screen bg-[#111111] py-12 px-4" data-testid="register-page">
      <div className="max-w-4xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Benefits Section */}
          <div className="lg:order-2">
            <div className="sticky top-24">
              <h2 className="font-manrope font-bold text-2xl text-white mb-6">
                Beneficios de la Membresía
              </h2>
              <div className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-amber-400" />
                    </div>
                    <p className="text-gray-300">{benefit}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
                <p className="text-amber-400 font-semibold mb-2">Membresía Anual</p>
                <p className="text-3xl font-manrope font-bold text-white">
                  $1,500 <span className="text-lg text-gray-400">MXN + IVA</span>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Vigencia de 12 meses desde la fecha de pago
                </p>
              </div>
            </div>
          </div>

          {/* Form Section */}
          <div className="lg:order-1">
            <div className="text-center lg:text-left mb-8">
              <Link to="/" className="inline-flex items-center gap-2 mb-6">
                <span className="font-manrope font-bold text-3xl text-white">CN</span>
                <span className="font-manrope font-bold text-3xl text-amber-400">I</span>
              </Link>
              <h1 className="font-manrope font-bold text-2xl text-white mb-2">
                Únete al Colegio
              </h1>
              <p className="text-gray-400">
                Crea tu cuenta y comienza tu proceso de certificación
              </p>
            </div>

            <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-8">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label htmlFor="nombre" className="text-gray-300">Nombre completo</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      id="nombre"
                      type="text"
                      required
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="pl-10 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="Tu nombre completo"
                      data-testid="register-nombre-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-gray-300">Correo electrónico</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="tu@email.com"
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="telefono" className="text-gray-300">Teléfono (opcional)</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      id="telefono"
                      type="tel"
                      value={formData.telefono}
                      onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                      className="pl-10 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="+52 55 1234 5678"
                      data-testid="register-telefono-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="estado" className="text-gray-300">Estado</Label>
                    <Input
                      id="estado"
                      type="text"
                      value={formData.estado}
                      onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                      className="mt-1 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="Ej: Ciudad de México"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ciudad" className="text-gray-300">Ciudad</Label>
                    <Input
                      id="ciudad"
                      type="text"
                      value={formData.ciudad}
                      onChange={(e) => setFormData({ ...formData, ciudad: e.target.value })}
                      className="mt-1 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="Ej: Benito Juárez"
                    />
                  </div>
                </div>

                {/* Asociaciones */}
                {associations.length > 0 && (
                  <div>
                    <Label className="text-gray-300 mb-3 block">
                      <Building2 className="w-4 h-4 inline mr-2" />
                      ¿Perteneces a alguna asociación inmobiliaria?
                    </Label>
                    <div className="grid grid-cols-1 gap-2 max-h-40 overflow-y-auto p-2 bg-[#111111] rounded-lg border border-[#2a2a2a]">
                      {associations.map((assoc) => (
                        <label 
                          key={assoc.id} 
                          className="flex items-center gap-3 p-2 rounded hover:bg-[#1a1a1a] cursor-pointer"
                        >
                          <Checkbox
                            checked={selectedAssociations.some(a => a.asociacion_id === assoc.id)}
                            onCheckedChange={() => toggleAssociation(assoc)}
                            className="border-[#3a3a3a] data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                          />
                          <span className="text-gray-300 text-sm">{assoc.nombre}</span>
                          <span className={`text-xs px-2 py-0.5 rounded ${assoc.estatus === 'Activa' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {assoc.estatus}
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      * Si seleccionas asociaciones, deberás subir el comprobante de pago de membresía
                    </p>
                  </div>
                )}

                <div>
                  <Label htmlFor="password" className="text-gray-300">Contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="Mínimo 6 caracteres"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="text-gray-300">Confirmar contraseña</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      className="pl-10 bg-[#111111] border-[#2a2a2a] text-white focus:border-amber-500"
                      placeholder="Repite tu contraseña"
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>

                {error && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400" data-testid="register-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-amber-500 hover:bg-amber-400 text-black font-semibold h-11"
                  data-testid="register-submit-btn"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creando cuenta...
                    </>
                  ) : (
                    'Crear Cuenta'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="text-amber-400 hover:text-amber-300 font-medium">
                    Inicia sesión
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
